/* eslint-disable @typescript-eslint/no-namespace */
import z from "zod";
import { parseStreetWithHouseNumber as parseStreetWithHouseNumberFn } from "./parse-street-house-number";

const AddressSchema = z.object({
    /**
     * The street name of the address.
     */
    street: z.string().trim().min(1, "Address: Invalid street.").brand<"Street">(),
    /**
     * The house number of the address.
     */
    houseNumber: z.string().trim().optional().brand<"HouseNumber">(),
    /**
     * The city of the address.
     */
    city: z.string().trim().min(1, "Address: Invalid city.").brand<"City">(),
    /**
     * The zip code of the address.
     */
    zip: z.string().trim().min(1, "Address: Invalid zip code.").brand<"Zip">(),
    /**
     * The state of the address.
     */
    state: z.string().trim().default("ZH").brand<"State">(),
    /**
     * The country of the address.
     */
    country: z.string().trim().default("Schweiz").brand<"Country">(),
});

/**
 * A postal address of a person or organization.
 *
 * The Address object is used to identify a customer organization.
 */
export type Address = z.infer<typeof AddressSchema>;

export namespace Address {
    /**
     * Creates a new Address object.
     *
     * @param value The value to create the Address object from.
     * @returns The created Address object.
     * @throws An error if the value is not a valid Address object.
     */
    export const create = (value: object): Address => {
        return AddressSchema.parse(value);
    };

    /**
     * Parses a street name and house number from a string.
     *
     * @param value The value to parse the street name and house number from.
     * @returns The parsed street name and house number.
     */
    export const parseStreetWithHouseNumber = parseStreetWithHouseNumberFn;

    /**
     * Checks if an address differs from another address.
     *
     * @param address The address to compare.
     * @param other The other address to compare with (can be a partial address).
     * @returns `true` if the addresses differ, `false` otherwise.
     */
    export const differs = (address: Address, other: Partial<Address>): boolean => {
        // Automatically compare all properties using reflection
        return Object.getOwnPropertyNames(address).some(key => {
            const prop = key as keyof Address;
            return other[prop] !== undefined && address[prop] !== other[prop];
        });
    };
}
